import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  DataTableGroupByDropdown,
  Dialog,
  DialogPopup,
  DialogTitle,
  IconTile,
  Text,
} from "@salestrak/ui";

import { useYieldToEntityDetail } from "#app/components/entity";
import { ManageDialogHeader } from "#app/components/manage-dialog-header";
import { CustomFieldsTable } from "#app/components/custom-fields/custom-fields-table";
import { FieldsManagerAction } from "#app/components/custom-fields/fields-manager-action";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { CUSTOM_FIELD_LIST } from "#app/lib/filters/list-configs";
import type { CustomFieldRow } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

const GROUP_BY_LABELS = {
  none: "None",
  type: "Type",
} as const;

/**
 * The desktop "add existing fields" picker: the Custom Field Manager's table
 * (search, group-by-type, the same columns) read as a checklist over the
 * fields the location does NOT carry yet. The attached ones are excluded in
 * the query (`where.excludeIds`), not dimmed in the list: the dialog is
 * titled with a verb, so every row is something it can do, and a checked
 * row that cannot be unchecked promises a removal this surface does not
 * offer — the location's own roster is where a field comes off. Rows toggle
 * into a draft that one footer commit applies. No location sidebar: the
 * dialog is always opened FROM a location, so the title carries the scope
 * and global browsing stays the manager's job.
 */
type AddCustomFieldsDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Field ids already attached to the location — left out of the list. */
  attachedFieldIds: string[];
  /** Commit: the picked rows, in pick order. The host writes each field's
   *  type default through its own value door and closes the dialog. */
  onAdd: (fields: CustomFieldRow[]) => void;
  /** "Create new" — the host closes this picker and opens the manager. */
  onCreateNew?: () => void;
  /** Names the scope in the header: "Add fields to {name}". */
  locationName?: string;
}>;

export function AddCustomFieldsContent({
  attachedIds,
  onAdd,
  onCreateNew,
  onClose,
  locationName,
}: Omittable<{
  attachedIds: ReadonlySet<string>;
  onAdd: (fields: CustomFieldRow[]) => void;
  onCreateNew?: () => void;
  onClose: () => void;
  locationName?: string;
}>) {
  const [groupByField, setGroupByField] =
    useState<keyof typeof GROUP_BY_LABELS>("type");
  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(CUSTOM_FIELD_LIST);
  // Memoized: the table derives one query variable object per type from it,
  // and a fresh object each render re-subscribes every group query.
  const pickableWhere = useMemo(
    () => ({ ...(where ?? {}), excludeIds: [...attachedIds] }),
    [where, attachedIds],
  );
  // The draft keeps whole rows, not ids: the commit needs each field's type
  // for its default value, and the row is already in hand at toggle time.
  const [selected, setSelected] = useState<Map<string, CustomFieldRow>>(
    () => new Map(),
  );

  const handleToggle = useCallback((field: CustomFieldRow) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(field.customFieldId)) next.delete(field.customFieldId);
      else next.set(field.customFieldId, field);
      return next;
    });
  }, []);

  const selection = useMemo(
    () => ({
      selectedIds: new Set(selected.keys()),
      onToggle: handleToggle,
    }),
    [selected, handleToggle],
  );

  const count = selected.size;

  return (
    <Box direction="column" fill="flex" overflow="hidden">
      <ManageDialogHeader
        onClose={onClose}
        // The manager is a sibling modal, so the picker closes before it opens.
        actions={<FieldsManagerAction onBeforeOpen={onClose} />}
      >
        <Box align="center" direction="row" gap="sm">
          <IconTile bordered name="swatch-book" size="sm" />
          <Text size="sm" weight="semibold">
            {locationName ? (
              <>
                Add fields to{" "}
                {/* The dashed underline marks the one variable word in the
                    title — which location this is (the stage dialog's
                    grammar; decoration classes = tracked gap, the Text
                    underline axis is solid-only). */}
                <Text
                  as="span"
                  size={null}
                  underline
                  className="decoration-dashed decoration-muted-foreground"
                >
                  {locationName}
                </Text>
              </>
            ) : (
              "Add fields"
            )}
          </Text>
        </Box>
      </ManageDialogHeader>

      <Box direction="column" fill="flex" overflow="hidden">
        <CustomFieldsTable
          where={pickableWhere}
          orderBy={orderBy}
          emptyMessage="Every field is already added"
          groupByField={groupByField}
          sorting={sorting}
          onSortingChange={setSorting}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          selection={selection}
          toolbarLeft={
            <DataTableGroupByDropdown
              groupByField={groupByField}
              setGroupByField={(value) =>
                setGroupByField(value as keyof typeof GROUP_BY_LABELS)
              }
              groupByLabels={GROUP_BY_LABELS}
            />
          }
          toolbarActions={
            onCreateNew ? (
              <Button size="xs" onClick={onCreateNew}>
                <Text size={null}>Create new</Text>
              </Button>
            ) : undefined
          }
        />
      </Box>

      {/* Commit footer — the count states the draft once, so the button
          stays a plain verb. */}
      <Box
        align="center"
        border="top"
        direction="row"
        gap="sm"
        justify="between"
        paddingX="md"
        paddingY="sm"
      >
        <Text size="sm" variant="muted">
          {count} field{count === 1 ? "" : "s"} selected
        </Text>
        <Box align="center" direction="row" gap="sm">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={count === 0}
            onClick={() => onAdd([...selected.values()])}
          >
            Add fields
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export function AddCustomFieldsDialog({
  open,
  onOpenChange,
  attachedFieldIds,
  onAdd,
  onCreateNew,
  locationName,
}: AddCustomFieldsDialogProps) {
  const attachedIds = useMemo(
    () => new Set(attachedFieldIds),
    [attachedFieldIds],
  );

  // A Created By chip press opens the profile island, which renders in the
  // shell beneath this modal — the dialog yields.
  useYieldToEntityDetail(open, () => onOpenChange(false));

  return (
    // Not responsive: the desktop table; the summary picker owns the phone sheet.
    <Dialog responsive={false} open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        // bg-background over the stock bg-popover — same body drop as the
        // manager, so the table reads on the page fill. (className = tracked
        // library gap.)
        className="bg-background"
        size="window"
        // Don't move initial focus onto the toolbar's first control — Chrome
        // paints a :focus-visible ring for that programmatic focus. Focus
        // stays trapped in the modal.
        initialFocus={false}
        showCloseButton={false}
      >
        <DialogTitle visuallyHidden>
          {locationName ? `Add fields to ${locationName}` : "Add fields"}
        </DialogTitle>
        <AddCustomFieldsContent
          attachedIds={attachedIds}
          onAdd={onAdd}
          onCreateNew={onCreateNew}
          onClose={() => onOpenChange(false)}
          locationName={locationName}
        />
      </DialogPopup>
    </Dialog>
  );
}
